import axios from "axios";
import API_BASE_URL from "../config/apiConfig";

const adminConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` },
});

// params is optional: { status } or { excludeStatus } narrows it to bookings in / not in that trip status.
export const listTripTrackingRows = async (params) => {
  const { data } = await axios.get(`${API_BASE_URL}/api/admin/trip-tracking-new`, { ...adminConfig(), params });
  return data || [];
};

// One trip for the timeline map: plan, stays, pins and route, even before the customer accepts the policy.
export const getAdminTripDetail = async (bookingId) => {
  const { data } = await axios.get(`${API_BASE_URL}/api/admin/trip-tracking-new/${bookingId}`, adminConfig());
  return data;
};

// Trips the office was emailed about for having no location, for the bookings page to sound on.
export const listSilenceAlerts = async () => {
  const { data } = await axios.get(`${API_BASE_URL}/api/admin/trip-tracking-new/silence-alerts`, adminConfig());
  return data || [];
};

// The first admin to click it is shown to everyone, and the page stops sounding for all of them.
export const markSilenceAlertSeen = async (alertId) => {
  const { data } = await axios.post(
    `${API_BASE_URL}/api/admin/trip-tracking-new/silence-alerts/${alertId}/seen`,
    null,
    adminConfig(),
  );
  return data;
};

// Trips past their pickup time with Trip Preparation not done, each with what is still missing.
export const listNotReadyAlerts = async () => {
  const { data } = await axios.get(`${API_BASE_URL}/api/admin/trip-tracking-new/not-ready-alerts`, adminConfig());
  return data || [];
};

export const markNotReadyAlertSeen = async (alertId) => {
  const { data } = await axios.post(
    `${API_BASE_URL}/api/admin/trip-tracking-new/not-ready-alerts/${alertId}/seen`,
    null,
    adminConfig(),
  );
  return data;
};

// Day 1 trips half an hour past reaching the stay with the balance still to pay.
export const listPaymentAlerts = async () => {
  const { data } = await axios.get(`${API_BASE_URL}/api/admin/trip-tracking-new/payment-alerts`, adminConfig());
  return data || [];
};

export const markPaymentAlertAsked = async (alertId) => {
  const { data } = await axios.post(
    `${API_BASE_URL}/api/admin/trip-tracking-new/payment-alerts/${alertId}/asked`,
    null,
    adminConfig(),
  );
  return data;
};

// The all-trips switch for the no-location check, and where it runs.
export const getSilenceCheck = async () => {
  const { data } = await axios.get(`${API_BASE_URL}/api/admin/trip-tracking-new/silence-check`, adminConfig());
  return data;
};

// On ties the check to the server answering - the live one the admin site talks to.
export const setSilenceCheck = async (on) => {
  const { data } = await axios.patch(`${API_BASE_URL}/api/admin/trip-tracking-new/silence-check`, { on }, adminConfig());
  return data;
};

// The Tracking Settings page: saved values (empty = default), the defaults, limits and the automatic marking switch.
export const getTrackingSettings = async () => {
  const { data } = await axios.get(`${API_BASE_URL}/api/admin/trip-tracking-new/settings`, adminConfig());
  return data;
};

// Every value as shown on the page; an empty one goes back to its default.
export const saveTrackingSettings = async (values) => {
  const { data } = await axios.put(`${API_BASE_URL}/api/admin/trip-tracking-new/settings`, values, adminConfig());
  return data;
};

// On ties automatic marking to the server answering, as with the no-location check.
export const setAutoMarking = async (on) => {
  const { data } = await axios.patch(`${API_BASE_URL}/api/admin/trip-tracking-new/settings/auto-marking`, { on }, adminConfig());
  return data;
};

// The switch on a bookings row: off stops this trip's no-location emails and page alarm, until turned back on.
export const setSilenceAlertsOff = async (bookingId, off) => {
  const { data } = await axios.patch(
    `${API_BASE_URL}/api/admin/trip-tracking-new/silence-alerts/bookings/${bookingId}`,
    { off },
    adminConfig(),
  );
  return data;
};

export const listTripTrackingBookings = async () => {
  const [bookingsRes, trackingRes] = await Promise.all([
    axios.get(`${API_BASE_URL}/api/admin/bookings`, adminConfig()),
    axios.get(`${API_BASE_URL}/api/admin/trip-tracking-new`, adminConfig()),
  ]);
  return {
    bookings: bookingsRes.data || [],
    trackingRows: trackingRes.data || [],
  };
};

export const driverLogin = async (username, password) => {
  const { data } = await axios.post(`${API_BASE_URL}/api/driver/auth/login`, { username, password });
  return data;
};

export const driverLinkLogin = async (accessKey) => {
  const { data } = await axios.post(`${API_BASE_URL}/api/driver/auth/link/${accessKey}`);
  return data;
};

export const listDriverTrips = async (driverId) => {
  const { data } = await axios.get(`${API_BASE_URL}/api/driver/trips`, { params: { driverId } });
  return data || { upcoming: [], ongoing: [], completed: [] };
};

export const getDriverTrip = async (driverId, bookingId) => {
  const { data } = await axios.get(`${API_BASE_URL}/api/driver/trips/${bookingId}`, { params: { driverId } });
  return data;
};

export const markDriverTracking = async ({ driverId, bookingId, eventType, placeKey, placeLabel, clientCapturedAt, latitude, longitude, accuracy, gpsProofType, gpsCapturedAt, odometerReading, odometerPhotoUrl }) => {
  const { data } = await axios.post(`${API_BASE_URL}/api/driver/trips/${bookingId}/tracking`, {
    driverId,
    eventType,
    placeKey,
    placeLabel,
    clientCapturedAt,
    latitude,
    longitude,
    accuracy,
    gpsProofType,
    gpsCapturedAt,
    odometerReading,
    odometerPhotoUrl,
  });
  return data;
};

// Sent as multipart because the blob comes straight off the camera canvas. Returns the CDN url
// that then travels with the tracking event.
export const uploadOdometerPhoto = async ({ driverId, bookingId, eventType, blob }) => {
  const form = new FormData();
  form.append("file", blob, "odometer.jpg");
  const { data } = await axios.post(
    `${API_BASE_URL}/api/driver/trips/${bookingId}/odometer-photo`,
    form,
    { params: { driverId, eventType } },
  );
  return data?.publicUrl || "";
};

export const uploadOdometerPhotoByToken = async (token, { eventType, blob }) => {
  const form = new FormData();
  form.append("file", blob, "odometer.jpg");
  const { data } = await axios.post(
    `${API_BASE_URL}/api/driver/booking/${token}/odometer-photo`,
    form,
    { params: { eventType } },
  );
  return data?.publicUrl || "";
};

export const saveDriverReadinessGps = async ({ driverId, bookingId, latitude, longitude, accuracy, clientCapturedAt, deviceInfo }) => {
  const { data } = await axios.post(`${API_BASE_URL}/api/driver/trips/${bookingId}/readiness/gps-test`, {
    driverId,
    latitude,
    longitude,
    accuracy,
    clientCapturedAt,
    deviceInfo,
  });
  return data;
};

export const saveDriverReadinessPractice = async ({ driverId, bookingId, clientCapturedAt, deviceInfo }) => {
  const { data } = await axios.post(`${API_BASE_URL}/api/driver/trips/${bookingId}/readiness/practice`, {
    driverId,
    clientCapturedAt,
    deviceInfo,
  });
  return data;
};

export const syncDriverLocations = async ({ driverId, bookingId, pings }) => {
  const { data } = await axios.post(`${API_BASE_URL}/api/driver/trips/${bookingId}/locations`, {
    driverId,
    pings,
  });
  return data;
};

export const getPublicTracking = async (token) => {
  const { data } = await axios.get(`${API_BASE_URL}/api/tracking/${token}`);
  return data;
};

// Returns the unlocked trip details once the policy is accepted.
export const acceptTripPolicy = async (token, { versionId, language, name, latitude, longitude, accuracyMeters }) => {
  const { data } = await axios.post(`${API_BASE_URL}/api/tracking/${token}/accept-policy`, {
    versionId,
    language,
    agreed: true,
    name,
    latitude,
    longitude,
    accuracyMeters,
  });
  return data;
};

// The page could not get the customer's location: reason is DENIED, UNAVAILABLE, TIMEOUT or UNSUPPORTED.
// The server reads the browser from the request. Best effort - a failed report is ignored.
export const reportPolicyLocationFailure = async (token, { reason, message }) => {
  try {
    await axios.post(`${API_BASE_URL}/api/tracking/${token}/location-failed`, { reason, message });
  } catch {
    // Nothing to tell the customer; they are already seeing the location help.
  }
};

// The customer's pickup spot and time. pickupTime is "HH:mm" on the booked pickup day, never earlier.
export const saveCustomerPickup = async (token, { latitude, longitude, link, landmark, contactPhone, pickupTime }) => {
  const { data } = await axios.put(`${API_BASE_URL}/api/tracking/${token}/pickup`, {
    latitude,
    longitude,
    link,
    landmark,
    contactPhone,
    pickupTime,
  });
  return data;
};

// Reads a pasted Google Maps link: { latitude, longitude }.
export const locatePickupLink = async (token, link) => {
  const { data } = await axios.post(`${API_BASE_URL}/api/tracking/${token}/pickup/locate`, { link });
  return data;
};

export const saveOfficePickup = async (bookingId, { latitude, longitude, link, landmark, contactPhone }) => {
  const { data } = await axios.put(
    `${API_BASE_URL}/api/admin/bookings/${bookingId}/pickup-point`,
    { latitude, longitude, link, landmark, contactPhone },
    adminConfig(),
  );
  return data;
};

export const getPickupHistory = async (bookingId) => {
  const { data } = await axios.get(`${API_BASE_URL}/api/admin/bookings/${bookingId}/pickup-point/history`, adminConfig());
  return data || [];
};

export const getDriverTripByToken = async (token) => {
  const { data } = await axios.get(`${API_BASE_URL}/api/driver/booking/${token}`);
  return data;
};

export const markDriverTrackingByToken = async (token, { eventType, placeKey, placeLabel, clientCapturedAt, latitude, longitude, accuracy, gpsProofType, gpsCapturedAt }) => {
  const { data } = await axios.post(`${API_BASE_URL}/api/driver/booking/${token}/tracking`, {
    eventType,
    placeKey,
    placeLabel,
    clientCapturedAt,
    latitude,
    longitude,
    accuracy,
    gpsProofType,
    gpsCapturedAt,
  });
  return data;
};

export const saveDriverReadinessGpsByToken = async (token, { latitude, longitude, accuracy, clientCapturedAt, deviceInfo }) => {
  const { data } = await axios.post(`${API_BASE_URL}/api/driver/booking/${token}/readiness/gps-test`, {
    latitude,
    longitude,
    accuracy,
    clientCapturedAt,
    deviceInfo,
  });
  return data;
};

export const saveDriverReadinessPracticeByToken = async (token, { clientCapturedAt, deviceInfo }) => {
  const { data } = await axios.post(`${API_BASE_URL}/api/driver/booking/${token}/readiness/practice`, {
    clientCapturedAt,
    deviceInfo,
  });
  return data;
};

export const syncDriverLocationsByToken = async (token, { pings }) => {
  const { data } = await axios.post(`${API_BASE_URL}/api/driver/booking/${token}/locations`, {
    pings,
  });
  return data;
};
